// @ts-strict-ignore
import stableStringify from 'json-stable-stringify';
import isPlainObject from 'lodash/isPlainObject';

/**
 * WIP
 * Base global store implementation that abstracts away api/caching/rehydration
 * for resources manipulated via API and UI.
 * @example
 * // will call callback every time data is made available (via cache, network,
 * // or setting explicitly in front-end code)
 * foobarStore.subscribe(foobar => doSomethingWith(foobar));
 */
import {getOwnAttr} from 'common/util';
import type {Subscribable} from 'common/types/context/subscribable';
import {purgeFetchCache} from 'common/report/queries/utils/fetch-cache';
import {ApiResponseError} from 'common/util/shared-client';

import {reporter} from '../error-reporting';
import {BaseClient, QueryOptions} from '../queries/utils/base-client';
import {deleteFromFetchCache} from '../queries/utils/fetch-cache';
import {warnDeveloper} from '../util';

type TransformType<StoredValue, RawValue> = {
  /**
   * transform GET API response into this.value
   */
  index?: (
    result: RawValue,
    {store, subscription}: {store?: Store<StoredValue, RawValue>; subscription?: Subscription},
  ) => StoredValue;
  /**
   * transform POST/PUT API response into a {k, v} map. v will be returned out of POST/PUT
   * and the store will patch {[k], v} in to this.value
   */
  update?: (
    result: RawValue,
    {store, subscription}: {store?: Store<StoredValue, RawValue>; subscription?: Subscription},
  ) => StoredValue;
};

export interface StoreOptions<StoredValue, RawValue> {
  apiClient: BaseClient;
  defaultValue: Partial<StoredValue>;
  name?: string;
  transform?: TransformType<StoredValue, RawValue>;
}

export interface Subscription {
  key: string;

  params: Obj;
  options: Obj;
  // Whenever the data changes or is deleted, the version will be incremented. This is useful for consumers that only want to
  // do something when data has changed
  version: number;

  initialized: boolean;
  fetchPromise: Promise<void>;
  callbackSet: Set<SubscriptionCallback>;
  triggeredCallbackSet: Set<SubscriptionCallback>;
}

export type FireSubscriptionCallbackParams = {
  cached?: boolean;
  error?: boolean;
  fromUpdate?: boolean;
  fromSharedValue?: boolean;
  /**
   * The store's value hasn't changed from the last time callbacks were fired (because we haven't fetched data or
   * the there was an error with fetching so we didn't replace the value).
   * NOTE: We don't currently detect if the fetched value has changed. So even if the fetched value hasn't changed,
   * unchangedValue will be false.
   * */
  unchangedValue?: boolean;
};

export type SubscriptionCallbackParams = FireSubscriptionCallbackParams & {
  subscription: Subscription;
  store: Store<any, any>;
};

export type SubscriptionCallback = (args: SubscriptionCallbackParams) => void;

/**
 * Factory for creating a BaseClient from a given URL to facilitate
 * easy Store usage until apiClient is migrated to use ApiClient directly
 *
 * @param urlFn a function that generates the url from params
 */
export function createSimpleClient(urlFn: (params: any) => string): BaseClient {
  return new (class extends BaseClient {
    getUrl(params) {
      return urlFn(params);
    }
    buildEndpoint(params) {
      return urlFn(params);
    }
    processResponse(response) {
      return response.results;
    }
  })();
}

/**
 * @deprecated
 * create a Model Context instead, e.g. `CurrentProjectResource`
 */
export class Store<Value, RawValue = Value> implements Subscribable {
  static getKey(params = {}, options = {}) {
    return stableStringify({params, options});
  }

  public declare value: Value;

  private apiClient = null;
  private transform: TransformType<Value, RawValue> = {};
  public name: string = null;
  private declare options: StoreOptions<Value, RawValue>;

  private subscriptions = new Map<string, Subscription>();
  private valueSharingStores = new Map<
    Store<any>,
    (valueUpdate: Partial<Value>) => Array<{key: string; valueUpdate: any}>
  >();

  constructor(options) {
    this.options = options;
    this.apiClient = this.options.apiClient;
    this.name = this.options.name;

    // TODO: remove this cast, defaultValue is only Partial<Value>
    this.value = this.options.defaultValue as Value;

    if (this.options.transform) {
      this.transform = this.options.transform;
    }
  }

  async fetch(params: {[key: string]: any} = {}, options = {}, key = ``) {
    let networkFaster = false;
    const subscription = this.getSubscription(key);
    let pendingFetchPromise: Subscription[`fetchPromise`] | null = null;

    if (subscription.initialized) {
      // give an update if we have initialized
      this.fireCallbacks(key, {unchangedValue: true});
    } else if (subscription.fetchPromise !== null) {
      pendingFetchPromise = subscription.fetchPromise;
    } else {
      subscription.params = params;
      subscription.options = options;

      const networkPromise = this.queryForSubscription(subscription, {updateFetchCache: true})
        .then((result: any) => {
          this.processQueryResult(result, subscription);
          networkFaster = true;
        })
        .catch((err: Error) => this.processQueryError(err, subscription));

      const fetchCachePromise = this.queryForSubscription(subscription, {fromFetchCache: true})
        .then((result: any) => {
          // only update store if fetch cache returns before network
          if (!networkFaster) {
            this.processQueryResult(result, subscription);
          }
        })
        .catch((err: Error) => this.processQueryError(err, subscription));

      // race both network and fetchCache, fall back to network if there was a miss on fetchCache
      subscription.fetchPromise = Promise.race([fetchCachePromise, networkPromise]);
      await subscription.fetchPromise;
      if (!subscription.initialized) {
        subscription.fetchPromise = networkPromise;
        await subscription.fetchPromise;
      }

      subscription.fetchPromise = null;
    }

    if (pendingFetchPromise !== null) {
      return pendingFetchPromise;
    }
  }

  async refresh(params = {}, options = {}, key = ``) {
    const subscription = this.getSubscription(key);

    if (!subscription.initialized) {
      subscription.params = params;
      subscription.options = options;
    }

    subscription.triggeredCallbackSet = new Set();
    await this.fetchForSubscription(subscription, {clearCache: true, updateFetchCache: true}, false);
  }

  incrementVersion(key = ``) {
    this.getSubscription(key).version += 1;
  }

  subscribe(callback, key = ``) {
    const subscription = this.getSubscription(key);
    if (!subscription.callbackSet.has(callback)) {
      subscription.callbackSet.add(callback);
    } else {
      warnDeveloper(`Store.subscribe: callback is already subscribed`, {storeName: this.name, key});
    }
    return this; // chainable
  }

  unsubscribe(callback, key = ``) {
    const subscription = this.subscriptions.get(key);
    if (subscription?.callbackSet.has(callback)) {
      subscription.callbackSet.delete(callback);
      subscription.triggeredCallbackSet.delete(callback);
    }
    return this;
  }

  unsubscribeAll(callback) {
    for (const subscription of this.subscriptions.values()) {
      if (subscription?.callbackSet.has(callback)) {
        subscription.callbackSet.delete(callback);
        subscription.triggeredCallbackSet.delete(callback);
      }
    }
  }

  fireCallbacks(key = ``, meta: FireSubscriptionCallbackParams = {}) {
    const subscription = this.getSubscription(key);
    for (const callback of subscription.callbackSet) {
      if (!subscription.triggeredCallbackSet.has(callback) || !meta.unchangedValue) {
        subscription.triggeredCallbackSet.add(callback);
        callback({...meta, subscription, store: this});
      }
    }
  }

  hasFetchedValue(key: any = ``) {
    return this.getSubscription(key).initialized;
  }

  async post(params, key = ``) {
    const result = await this.write(params, {method: `POST`}, key);
    this.invalidateFetchCache();
    return result;
  }

  async put(itemKey, params, key = ``) {
    const result = await this.write(params, {method: `PUT`}, key, itemKey);
    this.invalidateFetchCache();
    return result;
  }

  async patch(params, key = ``) {
    const result = await this.write(params, {method: `PATCH`}, key);
    this.invalidateFetchCache();
    return result;
  }

  async delete(itemKeyOrKeys, params, key = ``) {
    await this.apiClient.query(params, {clearCache: true, method: `DELETE`});

    const itemKeys = Array.isArray(itemKeyOrKeys) ? itemKeyOrKeys : [itemKeyOrKeys];
    itemKeys.forEach((itemKey) => delete this.value[itemKey]);
    this.incrementVersion(key);
    this.invalidateFetchCache();

    this.fireCallbacks(key);
  }

  /**
   * Performs a partial update for object like updates and a value
   * replacement for other type of updates
   *
   * @param valueUpdate update to patch or replace the store value
   */
  _update(valueUpdate: Partial<Value> = {}, key = ``) {
    if (Array.isArray(this.value)) {
      if (!Array.isArray(valueUpdate)) {
        throw new Error(`valueUpdate isn't an array but the store is`);
      }
      // the 'as unknown' is to work about the following:
      // TS2352: Conversion of type 'any[]' to type 'Value' may be a mistake because
      // neither type sufficiently overlaps with the other. If this was intentional,
      // convert the expression to 'unknown' first.
      this.value = valueUpdate as unknown as Value;
    } else if (isPlainObject(valueUpdate)) {
      // if it's an object, we support partial update
      this.value = {...this.value, ...valueUpdate};
    } else {
      this.value = valueUpdate as Value;
    }
    this.incrementVersion(key);
  }

  update(valueUpdate: Partial<Value>, key = ``) {
    if (Array.isArray(valueUpdate) || Object.keys(valueUpdate).length > 0) {
      this._update(valueUpdate, key);
      this.fireCallbacks(key, {fromUpdate: true});
      this.fireValueSharingCallbacks(valueUpdate);
    }
  }

  async refreshAll(params?) {
    // All sucscribers may not have attached themselves yet, so we purge the fetch cache so that they will get the latest values
    // when they do try, for exsisting subscriptions we can make sure they have the latest values cached.
    await purgeFetchCache({ttl: 0, path: `/${this.apiClient.buildEndpoint(params)}`});
    this.apiClient?.responseCache?.clearAll();

    await Promise.allSettled(
      Array.from(this.subscriptions).map(([key, subscription]) => {
        subscription.triggeredCallbackSet = new Set();
        let fetchPromise;
        if (subscription.callbackSet.size > 0) {
          fetchPromise = this.fetchForSubscription(subscription, {clearCache: true, updateFetchCache: true}, false);
        } else {
          if (!Array.isArray(this.value)) {
            // Clear the stale subscription's cached data for stores where we partition `value` by subscription key,
            // currently just propertyDefinitionsForEventsStore
            delete this.value[key];
            this.subscriptions.delete(key);
          }
          fetchPromise = Promise.resolve();
        }
        return fetchPromise;
      }),
    );
  }

  // useful for testing
  // it pierces 2 layers of caching which are both embedded within import-singletons (store and baseclient singletons) which persist across WCT tests
  public async reset(): Promise<void> {
    await this.invalidateFetchCache();
    this.subscriptions = new Map();
    // TODO: remove this cast, defaultValue is only Partial<Value>
    this.value = this.options.defaultValue as Value;
    this.apiClient.responseCache.clearAll();
  }

  public getSubscription(key) {
    let subscription = this.subscriptions.get(key);
    if (!subscription) {
      subscription = {
        key,
        params: null,
        options: null,
        initialized: false,
        fetchPromise: null,
        callbackSet: new Set(),
        triggeredCallbackSet: new Set(),
        version: 0,
      };
    }
    this.subscriptions.set(key, subscription);
    return subscription;
  }

  // Safe access a property on the store value, with an optional default there's no fetched value
  public get<K extends keyof NonNullable<Value>, V extends NonNullable<Value>[K]>(
    key: K,
    {defaultValue = null}: {defaultValue?: V} = {defaultValue: null},
  ): V | null {
    return this.hasFetchedValue(key) ? getOwnAttr(this.value, String(key)) : defaultValue;
  }

  private async invalidateFetchCache(): Promise<void> {
    for (const subscription of this.subscriptions.values()) {
      if (subscription.initialized && subscription.params && subscription.options) {
        await deleteFromFetchCache(
          this.apiClient.getUrl(subscription.params, {}),
          this.apiClient.getFetchOptions(subscription.params, subscription.options),
        );
      }
    }
  }

  private queryForSubscription(subscription: Subscription, extraOptions: QueryOptions, useQueryCache = null) {
    useQueryCache = useQueryCache !== null ? {useQueryCache} : {};
    return this.apiClient.query({...subscription.params, ...useQueryCache}, {...subscription.options, ...extraOptions});
  }

  private processQueryResult(result: RawValue, subscription: Subscription) {
    if (result) {
      const valueUpdate = (
        this.transform.index ? this.transform.index(result, {subscription, store: this}) : result
      ) as Partial<Value>;
      this._update(valueUpdate, subscription.key);
      subscription.initialized = true;
      this.fireCallbacks(subscription.key, {cached: true});
      this.fireValueSharingCallbacks(valueUpdate);
    }
  }

  private processQueryError(err: Error, subscription: Subscription) {
    this.fireCallbacks(subscription.key, {error: true, unchangedValue: true});

    reporter.error(err);
  }

  // TODO: consolidate `fetchForSubscription` and `write`. so far there is some divergence
  // - `fetchForSubscription` is catching and reporting errors to rollbar, whereas clients of `post` and
  // `put` expect and need error information to display on the UI
  // - the `index` and the `update` transform functions have different return formats
  // - `fetchForSubscription` sets this.initialized
  private async fetchForSubscription(subscription: Subscription, extraOptions: QueryOptions, useQueryCache = null) {
    try {
      const result = await this.queryForSubscription(subscription, extraOptions, useQueryCache);
      this.processQueryResult(result, subscription);
    } catch (err) {
      this.processQueryError(err, subscription);
    }
  }

  private async write(params, options: QueryOptions, key = ``, itemKey = null) {
    const result = await this.apiClient.query(params, options);
    if (result.status === `error`) {
      throw new ApiResponseError(result, result.error);
    }

    // 'itemKey' isn't technically necessary as client of `put` knows the itemKey and puts it into
    // `params`, but we add it to the `put` interface to mirror the `delete` interface
    const newResult = await (this.transform.update ? this.transform.update(result, itemKey) : result);
    const {k, v} = newResult;

    const valueUpdate = k ? ({[k]: v} as unknown as Value) : newResult;
    this._update(valueUpdate, key);

    this.fireCallbacks(key);
    this.fireValueSharingCallbacks(valueUpdate);
    return v ?? newResult;
  }

  // TODO async write (to api), invalidate

  private fireValueSharingCallbacks(valueUpdate: Partial<Value>): void {
    for (const [targetStore, getSharedValue] of this.valueSharingStores.entries()) {
      const sharedValues = getSharedValue(valueUpdate);
      targetStore.receiveSharedValues(sharedValues);
    }
  }

  public receiveSharedValues(sharedValues: Array<{key: string; valueUpdate: Partial<Value>}>): void {
    for (const sharedValue of sharedValues) {
      this._update(sharedValue.valueUpdate, sharedValue.key);
      this.fireCallbacks(sharedValue.key, {fromSharedValue: true});
    }
  }

  /**
   * Sets up a different Store to receive offline value updates derived from value updates to this Store.
   * @param targetStore a different Store for a value that can be derived from this Store's value
   * @param getSharedValue - a callback function that takes in a value update to this Store and outputs it into a list of value updates for the @targetStore
   */
  public shareValues<TargetStoreValue>(
    targetStore: Store<TargetStoreValue, unknown>,
    getSharedValue: (sourceValueUpdate: Partial<Value>) => Array<{key: string; valueUpdate: TargetStoreValue}>,
  ): void {
    this.valueSharingStores.set(targetStore, getSharedValue);
  }
}

export function waitForStoreValue(store: TODO, {timeout = 1_500, interval = 30} = {}): Promise<void> {
  const start = Date.now();
  return new Promise((resolve, reject) => {
    function checkValue() {
      if (store.hasFetchedValue()) {
        return resolve();
      }
      if (Date.now() - start > timeout) {
        return reject(new Error(`Timed out waiting for store value`));
      }
      setTimeout(checkValue, interval);
    }
    checkValue();
  });
}
